import { Backdrop, CircularProgress } from '@mui/material';
import * as React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { ErrorDialog } from '../components/error-dialog';
import { TopicPilotForm } from '../components/topic-pilot-form';
import { ErrorCode } from '../enums/ErrorCode';
import { ManualType } from '../enums/manualType';
import { SearchError } from '../enums/searchError';
import { CarModel, ManualModel, ManualsResultModel } from '../models';
import { IUrlParameters } from '../models/urlParameters';
import ServicesContext, { ServicesCollection } from './servicesContext';

export interface IOwnersManualsAppContext {
	model: CarModel,
	setModel: (model: CarModel, callbackHandler?: () => void) => void,
	isCurrentModel: boolean,
	hasDigitalManuals: boolean;
	carModels: CarModel[],
	hasError: boolean,
	setError: (titleKey: string, descriptionKey: string) => void,
	setTopicPilotData: (vin: string, partNumber: string, uiLanguage: string) => void,
	manuals: ManualModel[],
	loadManuals: (model: string, edition: string, market: string, language: string, vin: string) => Promise<ManualsResultModel>,
	initData: (urlParameters: IUrlParameters, afterCarModelInit?: (model: CarModel) => void) => void,
	loadCarModels: () => Promise<CarModel[]>,
	loadCarModelByVin: (vin: string) => Promise<CarModel>,
	handleSearchDataSubmit: (productionPeriod: string, market: string, language: string, hasDigitalManuals: boolean, history: any, afterSubmit?: () => void) => void,
	urlParameters: IUrlParameters,
	onError: (handler: (error: SearchError) => void) => void,
	errorCode: SearchError,
	clearUrlParameters: () => void,
	validate: (urlParameters: IUrlParameters) => void,
	services: ServicesCollection
}

const defaultContext: IOwnersManualsAppContext = {
	model: null,
	setModel: (model: CarModel) => Function(),
	isCurrentModel: true,
	carModels: [],
	hasDigitalManuals: false,
	hasError: false,
	setError: (titleKey: string, descriptionKey: string) => Function(),
	setTopicPilotData: (vin:string, partNumber: string, uiLanguage: string) => Function(),
	manuals: [],
	loadManuals: (model: string, edition: string, market: string, language: string, vin: string) => new Promise((resolve, reject) => resolve(null)),
	initData: (urlParameters: IUrlParameters, afterCarModelInit: (model: CarModel) => void) => Function(),
	loadCarModels: () => new Promise<CarModel[]>((resolve, reject) => resolve([])),
	loadCarModelByVin: (vin: string) => new Promise<CarModel>((resolve, reject) => resolve(null)),
	handleSearchDataSubmit: (productionPeriod: string, market: string, language: string, hasDigitalManuals: boolean, history: any, afterSubmit?: () => void) => Function(),
	urlParameters: null,
	onError: (handler: (error: SearchError) => void) => Function(),
	errorCode: SearchError.NoError,
	clearUrlParameters: () => Function(),
	validate: (urlParameters: IUrlParameters) => Function(),
	services: null
}

export const OwnersManualsAppContext: React.Context<IOwnersManualsAppContext> = React.createContext<IOwnersManualsAppContext>(defaultContext);

interface IOwnersManualsContextProps extends Partial<RouteComponentProps<any>> {}

interface IOwnersManualsContextState {
	model: CarModel;
	hasError: boolean;
	errorTitle: string;
	errorDescription: string;
	vin: string;
	partNumber: string;
	uiLanguage: string;
	hasDigitalManuals: boolean;
	manuals: ManualModel[],
	carModels: CarModel[],
	urlParameters: IUrlParameters,
	errorCode: SearchError,
	isLoading: boolean
}

class OwnersManualsContext extends React.Component<Partial<IOwnersManualsContextProps>, IOwnersManualsContextState> {
	static contextType = ServicesContext;
	context: React.ContextType<typeof ServicesContext>;

	onError: (error: SearchError) => void = undefined;

	constructor(props: any) {
		super(props);

		this.state = {
			model: null,
			hasError: false,
			errorTitle: "",
			errorDescription: "",
			vin: "",
			partNumber: "",
			uiLanguage: "",
			hasDigitalManuals: false,
			manuals: [],
			carModels: [],
			urlParameters: null,
			errorCode: SearchError.NoError,
			isLoading: false
		};
	}

	render(): any {
		return <OwnersManualsAppContext.Provider value={{
			model: this.state.model,
			setModel: (model: CarModel) => { this.setState({ ...this.state, model: model }) },
			hasError: this.state.hasError,
			setError: (titleKey: string, descriptionKey: string) => this.setState({ ...this.state, hasError: true, errorTitle: titleKey, errorDescription: descriptionKey }),
			setTopicPilotData: this.setTopicPilotData,
			hasDigitalManuals: this.state.hasDigitalManuals,
			loadManuals: this.loadManuals,
			manuals: this.state.manuals,
			carModels: this.state.carModels,
			initData: this.initData,
			loadCarModels: this.loadCarModels,
			loadCarModelByVin: this.loadCarModelByVin,
			handleSearchDataSubmit: this.handleSearchDataSubmit,
			urlParameters: this.state.urlParameters,
			onError: (handler: (error: SearchError) => void) => { this.onError = handler; },
			errorCode: this.state.errorCode,
			clearUrlParameters: () => this.setState({ ...this.state, urlParameters: null }),
			validate: this.validate,
			services: this.context,
			isCurrentModel: undefined
        }}>
			<React.Fragment>
				<Backdrop open={this.state.isLoading} style={{ zIndex: 10001 }}>
					<CircularProgress color="inherit" />
				</Backdrop>
				<TopicPilotForm vin={this.state.vin} partNumber={this.state.partNumber} uiLanguage={this.state.uiLanguage} bid={"004"} />
				{
					this.state.hasError &&
					<ErrorDialog
						headingText={this.context.resourceProvider.getText(this.state.errorTitle)}
						descriptionText={this.state.errorDescription ? this.context.resourceProvider.getText(this.state.errorDescription) : null}
						onClose={() => this.setState({ ...this.state, hasError: false, errorTitle: "", errorDescription: "" })}
					/>
				}
				{this.props.children}
			</React.Fragment>
		</OwnersManualsAppContext.Provider>
	}

	setTopicPilotData = (vin: string, partNumber: string, uiLanguage: string) =>
		this.setState({ ...this.state, manuals: [], vin: vin, partNumber: partNumber, uiLanguage: uiLanguage });

	showError = (errorCode?: SearchError, errorTitle?: string, errorDesc?: string) => {
		this.setState({ ...this.state, hasError: true, errorCode: errorCode ? errorCode : SearchError.NoError, errorTitle: errorTitle ? errorTitle : 'Search_Error_Title', errorDescription: errorDesc ? errorDesc : 'Search_Error_Description' });
		if (this.onError !== undefined) {
			this.onError(errorCode);
		}
		this.context.trackingPrepare.trackErrorPageView(this.state.model ? this.state.model.modelType : null);
	}

	handleSearchDataSubmit = async (productionPeriod: string, market: string, language: string, hasDigitalManuals: boolean, history: any, afterSubmit?: () => void) => {
		this.setTopicPilotData("", "", "");

		try {
			const manualsResult = await this.loadManuals(this.state.model ? this.state.model.modelType : "", productionPeriod, market, language, "");
			if (!manualsResult.results.length) {
				throw "emptyManualsResult";
			}
			if (manualsResult.results[0].manualType == ManualType.Digital) {
				this.context.trackingPrepare.trackOpenTPManual(this.state.model ? this.state.model.modelFamily : '', this.state.model ? this.state.model.modelType : '', manualsResult.results[0].digitalManual.partNumber);
				this.setTopicPilotData("", manualsResult.results[0].digitalManual.partNumber, manualsResult.results[0].digitalManual.uiLanguage);
				this.context.trackingPrepare.trackSearch(false, productionPeriod, market, language, this.state.model ? this.state.model.modelFamily : "", this.state.model ? this.state.model.modelType : "")
				return;
			}

			this.context.trackingPrepare.trackSearchResult(manualsResult.results.length.toString(), false, this.state.model ? this.state.model.modelFamily : "", this.state.model ? this.state.model.modelType : "");
			this.context.trackingPrepare.trackSearch(false, productionPeriod, market, language, this.state.model ? this.state.model.modelFamily : "", this.state.model ? this.state.model.modelType : "")

			const url = `/Detail?model=${this.state.model.modelType}&edition=${productionPeriod}&market=${market}&manualLang=${language}`;
			const routeData = {
				isCurrentModel: !this.state.model || this.state.model && !this.state.model.isOlder,
			};
			if (window.location.href.indexOf("/Detail") > -1) {
				history.replace(url, routeData);
			}
			else {
				setTimeout(() => history.push(url, routeData), 1000);
			}
		}
		catch (error) {
			this.showError();
		}

		if (afterSubmit) {
			afterSubmit();
		}
	}

	validate = (urlParameters: IUrlParameters) => {
		if (urlParameters.er === "1") {
			this.showError(undefined, 'Tool_Error_Title', 'Tool_Error_Description');
		}
	}

	initData = (urlParameters: IUrlParameters, afterModelInit?: (model: CarModel) => void) => {
		this.setState({ ...this.state, urlParameters: urlParameters }, async () => {
			if (!urlParameters.vin) {
				const carModels = await this.loadCarModels();
				const model = carModels.find(x => x.modelType == urlParameters.model);
				if (!model) {
					this.showError(SearchError.ModelNotFound, 'Search_Error_Title', 'Search_Error_Description');
				}
				this.setState({
					model: model
				}, () => {
					if (afterModelInit) {
						afterModelInit(model);
					}
				});
			}
			else {
				// Loading only for first init page load (direct page url access)
				if (!this.state.carModels || !this.state.carModels.length) {
					this.setState({ ...this.state, isLoading: true });
				}

				try {
					const model = await this.loadCarModelByVin(urlParameters.vin);
					if (!model) {
						throw ErrorCode.UnrecoginzedVin;
					}
					if (urlParameters.vin && urlParameters.manualLang) {
						try {
							const manualLang = urlParameters.manualLang ? urlParameters.manualLang : "";
							const manualsResult = await this.loadManuals("", "", "", manualLang, urlParameters.vin ? urlParameters.vin : "");
							if (!manualsResult.results.length) {
								throw SearchError.NoManuals;
							}

							this.setState({
								...this.state,
								isLoading: false
							});

							const partNumber = manualsResult.results[0].digitalManual ? manualsResult.results[0].digitalManual.partNumber : '';
							const uiLanguage = manualsResult.results[0].digitalManual ? manualsResult.results[0].digitalManual.uiLanguage : '';

							if (partNumber && uiLanguage) {
								this.context.trackingPrepare.trackOpenTPManual(model.modelFamily, model.modelType, manualsResult.results[0].digitalManual.partNumber);
								this.setTopicPilotData(urlParameters.vin, partNumber, uiLanguage);
								this.context.trackingPrepare.trackSearch(true, null, null, manualLang, model.modelFamily, model.modelType);
								return;
							}

							this.context.trackingPrepare.trackSearchResult(manualsResult.results.length.toString(), true, model.modelFamily, model.modelType);
							this.context.trackingPrepare.trackSearch(true, null, null, manualLang, model.modelFamily, model.modelType);
						}
						catch (error) {
							if (error == SearchError.RequestError) {
								this.showError();
							}
							this.setState({ ...this.state, isLoading: false });
						}
					}
					else {
						this.setState({ ...this.state, isLoading: false });
					}

					this.setState({ ...this.state, model: model }, () => {
						if (afterModelInit) {
							afterModelInit(model);
						}
					});

					this.loadCarModels();
				}
				catch (error) {
					this.showError(SearchError.UnrecoginzedVin, "Vin_Search_NotFound_Title", "Vin_Search_NotFound_Description");
					this.setState({ ...this.state, isLoading: false });
				}
			}
		});
	}

	loadCarModelByVin = async (vin: string): Promise<CarModel> => {
		try {
			const vinCarModelResult = await this.context.apiService.getCarModels(vin);
			if (!vinCarModelResult.length) {
				return null;
			}

			const model = vinCarModelResult[0];
			model.isFromVin = true;

			return model;
		}
		catch (error) {
			this.showError();
			throw error;
		}
	}

	loadCarModels = async (): Promise<CarModel[]> => {
		if (this.state.carModels.length > 0) {
			return this.state.carModels;
		}
		try {
			const result = await this.context.apiService.getCarModels();
			if (result.length > 0) {
				this.setState({ ...this.state, carModels: result });
			}
			else {
				this.showError();
			}
			return result;
		}
		catch (error) {
			this.showError();
			throw error;
		}
	}

	loadManuals = async (model: string, edition: string, market: string, language: string, vin: string): Promise<ManualsResultModel> => {
		try {
			const result = await this.context.apiService.getManuals(model, edition, market, language, vin);
			if (result.errorCode == ErrorCode.NoError || result.errorCode == ErrorCode.NoItems) {
				if (result.results.length && result.results[0].manualType == ManualType.Pdf) {
					this.setState({
						...this.state,
						hasDigitalManuals: false,
						model: vin ? this.state.model : this.state.carModels.find(x => x.modelType == model),
						manuals: result.results.length > 0 ? result.results : []
					});
				}
				return result;
			}
			throw SearchError.RequestError;
		}
		catch (error) {
			if (error == SearchError.RequestError) {
				this.showError();
			}
			throw error;
		}
	}
}

export default OwnersManualsContext;